import { ServiceClient } from 'src/utils'

const serviceClient = ServiceClient.singleton()

export const CRM_TYPE_SUGGESTIONS = ['PLOOMES', 'SMART_CRM'] as const

export const OVERFLOW_POLICIES = ['always_export', 'require_authorization', 'deny'] as const
export type OverflowPolicy = (typeof OVERFLOW_POLICIES)[number]

export type CrmDirection = 'inbound' | 'outbound' | 'both'

export type OutboundAuthType = 'api_key' | 'bearer' | 'header' | 'none'

export type OutboundAuth = {
    type: OutboundAuthType
    header_name?: string
}

export type OutboundHeader = {
    name: string
    value: string
}

export type OutboundBodyAttribute = {
    path: string
    value: string
}

export type FieldMappingEntry = {
    source: string
    target: string
}

export type OutboundConfig = {
    endpoint_base?: string
    path?: string
    method?: string
    auth_token?: string
    auth?: OutboundAuth
    headers?: OutboundHeader[]
    body_attributes?: OutboundBodyAttribute[]
    field_mapping?: FieldMappingEntry[]
}

export type CrmConnection = {
    id: number
    group_id: number
    crm_type: string
    name: string
    direction: CrmDirection
    outbound_config: OutboundConfig | null
    active: boolean
    created_at: string
    updated_at: string | null
}

export type CreateCrmConnectionInput = {
    crm_type: string
    name: string
    direction?: CrmDirection
    outbound_config?: OutboundConfig
}

export type UpdateCrmConnectionInput = Partial<CreateCrmConnectionInput> & { active?: boolean }

export type ApiKeyStatus = 'active' | 'expired' | 'revoked'

export type ApiKeyMetadata = {
    id: number
    connection_id: number
    connection_name: string | null
    name: string
    key_prefix: string
    expires_at: string | null
    overflow_policy: OverflowPolicy
    active: boolean
    created_at: string
    last_used_at: string | null
    revoked_at: string | null
    status: ApiKeyStatus
}

export type GeneratedApiKey = {
    id: number
    group_id: number
    connection_id: number
    name: string
    apiKey: string
    key_prefix: string
    expires_at: string | null
    overflow_policy: OverflowPolicy
    active: boolean
    created_at: string
}

export type CreateApiKeyInput = {
    name: string
    expiresAt?: string
    overflowPolicy?: OverflowPolicy
}

export type UpdateApiKeyInput = {
    name?: string
    overflowPolicy?: OverflowPolicy
}

export type ExportRequestFlow = 'push' | 'pull_sync' | 'pull_async'

export type ExportRequestStatus = 'completed' | 'pending_authorization' | 'authorized' | 'denied_by_config' | 'failed'

export type ExportRequestSummary = {
    id: number
    groupId: number
    connectionId: number | null
    apiKeyId: number | null
    requestedBy: number | null
    requestedByName: string | null
    flow: ExportRequestFlow
    cnpjsCount: number
    status: ExportRequestStatus
    overflowPolicy: OverflowPolicy | null
    franchiseOver: boolean
    authorizedBy: number | null
    authorizedAt: string | null
    deniedReason: string | null
    pushedAt: string | null
    fetchedAt: string | null
    createdAt: string
    updatedAt: string | null
    connectionName: string | null
    connectionCrmType: string | null
}

export type ExportRequestListFilter = {
    status?: ExportRequestStatus
    startMonth?: string
    endMonth?: string
    page?: number
    limit?: number
}

export type ExportRequestListResponse = {
    records: ExportRequestSummary[]
    totalRecords: number
}

export type AuthorizeResult = {
    requestId: number
    status: ExportRequestStatus
    total: number
    pushed: boolean
    debited: boolean
}

export type ReprocessResult = {
    requestId: number
    jobId: number
    requeued: boolean
    deduped: boolean
}

export const CONTRATO_FIELD_KEYS: readonly string[] = [
    'cnpj',
    'razao_social',
    'nome_fantasia',
    'data_abertura',
    'situacao_cadastral',
    'estimativa_vidas',
    'faturamento_estimado_faixa',
    'cnae_principal_codigo',
    'cnae_principal_descricao',
    'endereco_logradouro',
    'endereco_bairro',
    'endereco_cidade',
    'endereco_uf',
    'endereco_cep',
    'contato_telefone_principal',
    'contato_email_institucional',
    'natureza_juridica',
    'decisor_nome',
    'decisor_cargo',
    'midia_website'
]

export const MASKED_SECRET = '••••'

export function ehAdminDoGrupo(
    userName: string | undefined | null,
    profile: { admin_name?: string; admin_fullname?: string } | undefined | null
): boolean {
    if (!userName || !profile) {
        return false
    }
    return userName === profile.admin_name || userName === profile.admin_fullname
}

const BASE = '/api/v1/integration/groups'

export class CrmIntegrationService {
    static #INSTANCE = new CrmIntegrationService()
    static readonly singleton = () => CrmIntegrationService.#INSTANCE

    async listConnections(groupId: number): Promise<CrmConnection[]> {
        return serviceClient.authGet<CrmConnection[]>(`${BASE}/${groupId}/connections`)
    }

    async createConnection(groupId: number, input: CreateCrmConnectionInput): Promise<CrmConnection> {
        return serviceClient.authPost<CrmConnection>(`${BASE}/${groupId}/connections`, input)
    }

    async updateConnection(
        groupId: number,
        connectionId: number,
        input: UpdateCrmConnectionInput
    ): Promise<CrmConnection> {
        return serviceClient.authPatch<CrmConnection>(`${BASE}/${groupId}/connections/${connectionId}`, input)
    }

    async deleteConnection(groupId: number, connectionId: number): Promise<{ success: true }> {
        return serviceClient.authDelete<{ success: true }>(`${BASE}/${groupId}/connections/${connectionId}`)
    }

    async listApiKeys(groupId: number): Promise<ApiKeyMetadata[]> {
        return serviceClient.authGet<ApiKeyMetadata[]>(`${BASE}/${groupId}/api-keys`)
    }

    async generateApiKey(groupId: number, connectionId: number, input: CreateApiKeyInput): Promise<GeneratedApiKey> {
        return serviceClient.authPost<GeneratedApiKey>(`${BASE}/${groupId}/connections/${connectionId}/api-keys`, input)
    }

    async updateApiKey(groupId: number, apiKeyId: number, input: UpdateApiKeyInput): Promise<ApiKeyMetadata> {
        return serviceClient.authPatch<ApiKeyMetadata>(`${BASE}/${groupId}/api-keys/${apiKeyId}`, input)
    }

    async revokeApiKey(groupId: number, apiKeyId: number): Promise<{ success: true }> {
        return serviceClient.authDelete<{ success: true }>(`${BASE}/${groupId}/api-keys/${apiKeyId}`)
    }

    async listExportRequests(
        groupId: number,
        filter: ExportRequestListFilter = {}
    ): Promise<ExportRequestListResponse> {
        const params = new URLSearchParams()
        filter.status && params.append('status', filter.status)
        filter.startMonth && params.append('startMonth', filter.startMonth)
        filter.endMonth && params.append('endMonth', filter.endMonth)
        filter.page != null && params.append('page', String(filter.page))
        filter.limit != null && params.append('limit', String(filter.limit))
        const qs = params.toString()
        return serviceClient.authGet<ExportRequestListResponse>(
            `${BASE}/${groupId}/export-requests${qs ? `?${qs}` : ''}`
        )
    }

    async authorizeExportRequest(groupId: number, requestId: number): Promise<AuthorizeResult> {
        return serviceClient.authPost<AuthorizeResult>(`${BASE}/${groupId}/export-requests/${requestId}/authorize`)
    }

    async reprocessExportRequest(groupId: number, requestId: number): Promise<ReprocessResult> {
        return serviceClient.authPost<ReprocessResult>(`${BASE}/${groupId}/export-requests/${requestId}/reprocess`)
    }
}

export default CrmIntegrationService.singleton()
